import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Lock, Zap, Crown, X, ArrowRight, CheckCircle2, Sparkles, AlertCircle, RefreshCcw } from 'lucide-react';
import { motion } from 'motion/react';
import { MembershipTier, canAccessTier, UserProfile } from '../types';
import { auth } from '../firebase';

// ─── 등급 색상/아이콘 설정 ────────────────────────────────────────────────────

const TIER_CONFIG: Record<MembershipTier, {
  label: string;
  icon: React.ReactNode;
  color: string;
  bgGradient: string;
  borderColor: string;
  glow: string;
  price: string;
  perks: string[];
}> = {
  TIER1: {
    label: 'Tier 1 (주린이)',
    icon: <Zap className="w-4 h-4 text-slate-400" />,
    color: 'text-slate-400',
    bgGradient: 'from-slate-500/20 to-slate-600/10',
    borderColor: 'border-slate-500/30',
    glow: 'shadow-slate-500/10',
    price: '무료',
    perks: ['보유종목 5개 수동 등록', '관심종목 10개', '뉴스 분석 월 5회', '모의투자 Sandbox 지원'],
  },
  TIER2: {
    label: 'Tier 2 (새싹 투자자)',
    icon: <Zap className="w-4 h-4 text-amber-400" />,
    color: 'text-amber-400',
    bgGradient: 'from-amber-500/20 to-yellow-600/10',
    borderColor: 'border-amber-500/30',
    glow: 'shadow-amber-500/10',
    price: '₩9,900/월',
    perks: ['보유종목 15개', '뉴스 분석 월 20회', 'AI 포트폴리오 진단 월 3회', '모의 포트폴리오 심층 진단', '매크로 리스크 알림'],
  },
  TIER3: {
    label: 'Tier 3 (시장 참여자)',
    icon: <Zap className="w-4 h-4 text-emerald-400" />,
    color: 'text-emerald-400',
    bgGradient: 'from-emerald-500/20 to-teal-600/10',
    borderColor: 'border-emerald-500/30',
    glow: 'shadow-emerald-500/10',
    price: '₩24,900/월',
    perks: ['보유종목 30개', '뉴스 분석 월 50회', 'AI 포트폴리오 진단 월 10회', '다중 모의 시나리오 (최대 3개)', 'AI 비중 편차 시뮬레이션 지원'],
  },
  TIER4: {
    label: 'Tier 4 (전략가)',
    icon: <Crown className="w-4 h-4 text-blue-400" />,
    color: 'text-blue-400',
    bgGradient: 'from-blue-500/20 to-indigo-600/10',
    borderColor: 'border-blue-500/30',
    glow: 'shadow-blue-500/15',
    price: '₩49,000/월',
    perks: ['보유종목 100개', '뉴스 분석 월 150회', 'AI 포트폴리오 진단 월 30회', 'AI 개인화 부가기능 기본 포함', '공매도/옵션 퀀트 모델 보고서'],
  },
  TIER5: {
    label: 'Tier 5 (프로)',
    icon: <Crown className="w-4 h-4 text-violet-400" />,
    color: 'text-violet-400',
    bgGradient: 'from-violet-500/20 to-purple-600/10',
    borderColor: 'border-violet-500/30',
    glow: 'shadow-violet-500/25',
    price: '₩89,000/월',
    perks: ['모든 한도 무제한', '전 AI 모델 자유 선택 가능', '매크로 숏 시뮬레이션 백테스트', 'Raw Data API 제공', '부가서비스 3종 전체 번들 제공'],
  },
};

interface UpgradeModalProps {
  requiredTier: MembershipTier;
  featureName: string;
  currentTier: MembershipTier;
  userProfile?: UserProfile | null;
  billingCycle?: 'monthly' | 'annual';
  onClose: () => void;
  onUpgradeSuccess?: () => void;
  onNavigateTo?: (tab: string) => void;
}

export function UpgradeModal({
  requiredTier,
  featureName,
  currentTier,
  userProfile,
  billingCycle = 'monthly',
  onClose,
  onUpgradeSuccess,
  onNavigateTo,
}: UpgradeModalProps) {
  const [activeStep, setActiveStep] = useState<'checkout' | 'success' | 'fail'>('checkout');
  const [selectedBillingCycle, setSelectedBillingCycle] = useState<'monthly' | 'annual'>(billingCycle);
  const [previewData, setPreviewData] = useState<any>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(true);
  const [isPaying, setIsPaying] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [paymentType, setPaymentType] = useState<'subscription' | 'onetime'>('subscription');

  // 1. 일할 계산(Proration) 정보 비동기 로드
  useEffect(() => {
    async function loadProrationPreview() {
      try {
        setIsLoadingPreview(true);
        const token = await auth.currentUser?.getIdToken();
        const lang = userProfile?.uiLanguage || 'ko';
        const res = await fetch(`/api/payment/proration-preview?targetTier=${requiredTier}&billingCycle=${selectedBillingCycle}&lang=${lang}`, {
          headers: {
            'Authorization': `Bearer ${token || ''}`
          }
        });
        if (!res.ok) throw new Error('Failed to preview pricing');
        const data = await res.json();
        setPreviewData(data);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoadingPreview(false);
      }
    }
    loadProrationPreview();
  }, [requiredTier, selectedBillingCycle, userProfile]);

  const targetCfg = TIER_CONFIG[requiredTier] || TIER_CONFIG.TIER3;
  const currentCfg = TIER_CONFIG[currentTier] || TIER_CONFIG.TIER1;

  const getTierDisplayPrice = (tierKey: MembershipTier) => {
    const isKo = userProfile?.uiLanguage === 'ko';
    const isAnnual = selectedBillingCycle === 'annual';
    const useUSD = previewData ? previewData.currency === 'USD' : !isKo;

    if (tierKey === 'TIER1') {
      return isKo ? '무료' : 'Free';
    }

    // Single Source of Truth: previewData에서 가격 정보 사용
    // previewData는 /api/payment/proration-preview → 백엔드 getTierPrice() → DB subscription_products 경로로 결정됨
    if (previewData) {
      // tierKey가 현재 등급이면 currentPrice, 대상 등급이면 targetPrice 사용
      const isCurrentTier = tierKey === currentTier;
      const amount = isCurrentTier
        ? (previewData.currentPrice !== undefined ? previewData.currentPrice : 0)
        : (previewData.targetPrice !== undefined ? previewData.targetPrice : 0);

      if (useUSD) {
        return isAnnual ? `$${amount.toFixed(2)}/년` : `$${amount.toFixed(2)}/월`;
      } else {
        return isAnnual ? `₩${amount.toLocaleString()}/년` : `₩${amount.toLocaleString()}/월`;
      }
    }

    // Fallback: proration-preview API 실패 시에만 정적 값 사용 (API 캐시 TTL 내 재시도)
    if (useUSD) {
      return isAnnual ? `$0/년` : `$0/월`;
    } else {
      return isAnnual ? `₩0/년` : `₩0/월`;
    }
  };

  // 2. 기존 등록 카드로 원클릭 즉시 결제 처리
  const handleDirectProratedUpgrade = async () => {
    try {
      setIsPaying(true);
      setErrorMessage('');
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('인증 토큰이 유효하지 않습니다.');

      const res = await fetch('/api/payment/billing/upgrade-prorated', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          targetTier: requiredTier,
          billingCycle: selectedBillingCycle,
          lang: userProfile?.uiLanguage || 'ko'
        })
      });

      const data = await res.json() as any;
      if (!res.ok) {
        throw new Error(data.error || '결제에 실패했습니다.');
      }

      // 프로필 갱신 콜백 호출
      if (onUpgradeSuccess) {
        onUpgradeSuccess();
      }
      setActiveStep('success');
    } catch (err: any) {
      console.error(err);
      setErrorMessage(err.message || '카드 결제 승인에 실패했습니다.');
      setActiveStep('fail');
    } finally {
      setIsPaying(false);
    }
  };

  // 3. 카드 신규 등록창 호출 (기존 카드 미보유 시)
  const handleTossBillingAuthCall = async () => {
    try {
      setIsPaying(true);
      setErrorMessage('');
      const clientKey = import.meta.env.VITE_TOSS_CLIENT_KEY || 'test_ck_BX7zk2yd8yYgaWXMlByB8x9POLqK';
      const tossPayments = (window as any).TossPayments?.(clientKey);
      if (!tossPayments) {
        throw new Error('결제 모듈을 로드하지 못했습니다.');
      }

      await tossPayments.requestBillingAuth('카드', {
        customerKey: userProfile?.userId || 'guest',
        successUrl: `${window.location.origin}?billing=success&tier=${requiredTier}&billingCycle=${selectedBillingCycle}&prorated=true&tab=mypage`,
        failUrl: `${window.location.origin}?billing=fail`,
        customerEmail: userProfile?.email || '',
        customerName: userProfile?.displayName || '사용자',
      });
    } catch (err: any) {
      setErrorMessage(err.message || '카드 등록창 열기에 실패했습니다.');
      setActiveStep('fail');
      setIsPaying(false);
    }
  };

  const handleTossOneTimePaymentCall = async () => {
    try {
      setIsPaying(true);
      setErrorMessage('');
      const clientKey = import.meta.env.VITE_TOSS_CLIENT_KEY || 'test_ck_BX7zk2yd8yYgaWXMlByB8x9POLqK';
      const tossPayments = (window as any).TossPayments?.(clientKey);
      if (!tossPayments) {
        throw new Error('결제 모듈을 로드하지 못했습니다.');
      }

      // Single Source of Truth: proration-preview API 응답의 amountToPay 사용
      // DB subscription_products → 백엔드 getTierPrice() 및 일할 계산을 거쳐 결정된 오늘 결제할 금액
      const amount = previewData?.amountToPay !== undefined ? previewData.amountToPay : (previewData?.targetPrice || 0);
      if (amount <= 0) {
        throw new Error('유효한 결제 금액을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.');
      }

      const targetTierName = KOREAN_TIER_NAMES[requiredTier] || requiredTier;
      const orderName = selectedBillingCycle === 'annual'
        ? `${targetTierName} 1년 (일회성)`
        : `${targetTierName} 30일권`;

      await tossPayments.requestPayment('CARD', {
        amount,
        orderId: `one_${Date.now()}_${userProfile?.userId?.slice(0, 8)}`,
        orderName,
        successUrl: `${window.location.origin}?payment=success&tier=${requiredTier}&billingCycle=${selectedBillingCycle}`,
        failUrl: `${window.location.origin}?payment=fail`,
        customerEmail: userProfile?.email || '',
        customerName: userProfile?.displayName || '사용자',
      });
    } catch (err: any) {
      setErrorMessage(err.message || '결제창 열기에 실패했습니다.');
      setActiveStep('fail');
      setIsPaying(false);
    }
  };

  const getProratedReceiptText = () => {
    if (!previewData) return null;
    const isKo = userProfile?.uiLanguage === 'ko';

    // 포맷팅
    const formatMoney = (val: number) => {
      if (previewData.currency === 'KRW') {
        return `₩${val.toLocaleString()}`;
      } else {
        return `$${val.toFixed(2)}`;
      }
    };

    return {
      targetPrice: formatMoney(previewData.targetPrice),
      refundAmount: formatMoney(previewData.refundAmount),
      amountToPay: formatMoney(previewData.amountToPay),
      nextBillingDate: previewData.nextBillingDate ? previewData.nextBillingDate.slice(0, 10) : ''
    };
  };

  const KOREAN_TIER_NAMES: Record<MembershipTier, string> = {
    TIER1: '주린이',        // 무료 (Tier 1)
    TIER2: '새싹 투자자',   // ₩9,900/월 (Tier 2)
    TIER3: '시장 참여자',   // ₩24,900/월 (Tier 3)
    TIER4: '전략가',        // ₩49,000/월 (Tier 4)
    TIER5: '프로'           // ₩89,000/월 (Tier 5)
  };

  const currentTierName = (currentTier && KOREAN_TIER_NAMES[currentTier]) || currentCfg?.label || currentTier || '현재 등급';
  const targetTierName = (requiredTier && KOREAN_TIER_NAMES[requiredTier]) || targetCfg?.label || requiredTier || '업그레이드 등급';

  const receipt = getProratedReceiptText();

  return createPortal(
    <div className="fixed inset-0 z-[200] bg-black/85 backdrop-blur-sm overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4 py-8 sm:p-6">
        <div className="relative w-full max-w-4xl bg-zinc-950 border border-white/10 rounded-3xl overflow-hidden shadow-2xl p-6 sm:p-8 flex flex-col gap-6">

          {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/30 hover:text-white/70 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {activeStep === 'checkout' && (
          <div className="w-full flex flex-col gap-6">
            {/* ── 멤버십 등급 업그레이드 혜택 안내 배너 (최상단 고정) ── */}
            <div className="w-full bg-gradient-to-r from-indigo-500/15 via-purple-500/10 to-violet-500/15 border border-indigo-500/30 p-5 rounded-2xl flex items-start gap-3.5 text-left transition-all duration-300 animate-in fade-in slide-in-from-top-4 shadow-lg shadow-indigo-500/5">
              <div className="p-2.5 rounded-xl bg-indigo-500/20 text-indigo-300 shadow-md shadow-indigo-500/20">
                <Sparkles className="w-5 h-5 shrink-0 animate-pulse" />
              </div>
              <div className="space-y-1.5 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="text-sm font-extrabold text-white flex items-center gap-1.5">
                    {userProfile?.uiLanguage === 'ko'
                      ? `✨ ${targetTierName} 등급 업그레이드 혜택 안내`
                      : `✨ Upgrade to ${targetTierName} & Unlock Full Features`}
                  </h4>
                  <span className="text-[10px] bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 font-extrabold px-2 py-0.5 rounded-full tracking-wide">
                    {userProfile?.uiLanguage === 'ko' ? '혜택 해금' : 'Perks Unlocked'}
                  </span>
                </div>
                <p className="text-xs text-white/80 leading-relaxed font-medium">
                  {userProfile?.uiLanguage === 'ko' ? (
                    <>
                      현재 <span className="text-amber-300 font-extrabold">{currentTierName}</span> 등급을 이용 중입니다.{' '}
                      <span className="text-indigo-300 font-extrabold">{targetTierName}</span> 등급으로 업그레이드하시면{' '}
                      <span className="text-emerald-400 font-extrabold">[{featureName}]</span> 기능을 포함한 최상위 분석 도구와 확충된 한도를 즉시 이용하실 수 있습니다.
                    </>
                  ) : (
                    <>
                      You are currently on <span className="text-amber-300 font-extrabold">{currentTierName}</span>.{' '}
                      Upgrading to <span className="text-indigo-300 font-extrabold">{targetTierName}</span> instantly unlocks{' '}
                      <span className="text-emerald-400 font-extrabold">[{featureName}]</span> along with enhanced AI analytics and expanded limits.
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* ── 등급 전환 요약 헤더 (어떤 등급으로 변경되는지 명확히 표시) ── */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-5 rounded-2xl bg-gradient-to-r from-indigo-500/10 via-violet-500/8 to-transparent border border-indigo-500/20">
              {/* 현재 등급 */}
              <div className="flex flex-col items-center gap-1.5 min-w-[90px]">
                <span className="text-[9px] font-black uppercase tracking-widest text-white/25">현재 등급</span>
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-bold ${currentCfg.borderColor} bg-white/[0.03]`}>
                  {currentCfg.icon}
                  <span className={currentCfg.color}>{currentTierName}</span>
                </div>
                <span className="text-[10px] text-white/30 font-mono">{getTierDisplayPrice(currentTier)}</span>
              </div>

              {/* 전환 화살표 */}
              <div className="flex sm:flex-col items-center gap-1 mx-1">
                <div className="w-8 h-px bg-gradient-to-r from-white/10 to-indigo-400/60 sm:w-px sm:h-8 sm:bg-gradient-to-b" />
                <ArrowRight className="w-5 h-5 text-indigo-400 shrink-0" />
                <div className="w-8 h-px bg-gradient-to-r from-indigo-400/60 to-white/10 sm:w-px sm:h-8 sm:bg-gradient-to-b" />
              </div>

              {/* 대상 등급 (업그레이드 목표) */}
              <div className="flex flex-col items-center gap-1.5 min-w-[110px]">
                <span className="text-[9px] font-black uppercase tracking-widest text-indigo-300/70">업그레이드 등급</span>
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-black ${targetCfg.borderColor} bg-gradient-to-r ${targetCfg.bgGradient} shadow-lg ${targetCfg.glow}`}>
                  {targetCfg.icon}
                  <span className={targetCfg.color}>{targetTierName}</span>
                </div>
                <span className="text-[10px] text-indigo-300/60 font-mono font-bold">{getTierDisplayPrice(requiredTier)}</span>
              </div>

              {/* 설명 텍스트 */}
              <div className="flex-1 hidden sm:block">
                <p className="text-[11px] text-white/40 leading-relaxed text-left">
                  {userProfile?.uiLanguage === 'ko'
                    ? <><span className={`font-bold ${targetCfg.color}`}>{targetTierName}</span> 멤버십으로 변경 및 결제가 진행됩니다.<br />아래 영수증에서 실제 결제금액을 확인하세요.</>
                    : <>You are upgrading to <span className={`font-bold ${targetCfg.color}`}>{targetTierName}</span> membership.<br />Review the prorated invoice below before confirming.</>}
                </p>
              </div>
            </div>

            {/* ── 결제 주기 선택기 (월간 / 연간 결제 토글) ── */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white/[0.02] border border-white/5 p-4 rounded-2xl">
              <div className="text-left">
                <p className="text-xs font-bold text-white">
                  {userProfile?.uiLanguage === 'ko' ? '결제 주기 및 할인 선택' : 'Choose Billing Cycle & Discount'}
                </p>
                <p className="text-[10px] text-white/45 mt-0.5">
                  {userProfile?.uiLanguage === 'ko'
                    ? '연간 구독 선택 시 월간 결제 총합 대비 16.7% 할인을 상시 제공합니다.'
                    : 'Choose annual billing to receive a constant 16.7% discount compared to monthly billing.'}
                </p>
              </div>
              <div className="flex p-1 rounded-xl bg-zinc-900 border border-white/5 self-start sm:self-auto">
                <button
                  onClick={() => setSelectedBillingCycle('monthly')}
                  className={`px-4 py-1.5 rounded-lg text-xs font-black transition-all ${selectedBillingCycle === 'monthly'
                    ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-md'
                    : 'text-white/40 hover:text-white/70'
                    }`}
                >
                  {userProfile?.uiLanguage === 'ko' ? '월간 결제' : 'Monthly'}
                </button>
                <button
                  onClick={() => setSelectedBillingCycle('annual')}
                  className={`px-4 py-1.5 rounded-lg text-xs font-black transition-all flex items-center gap-1.5 ${selectedBillingCycle === 'annual'
                    ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-md'
                    : 'text-white/40 hover:text-white/70'
                    }`}
                >
                  <span>{userProfile?.uiLanguage === 'ko' ? '연간 결제' : 'Annual'}</span>
                  <span className="text-[9px] bg-emerald-500/20 text-emerald-300 font-extrabold px-1.5 py-0.5 rounded tracking-wide animate-pulse">
                    16.7% Off
                  </span>
                </button>
              </div>
            </div>

            {/* ── 결제 방식 선택기 (정기 결제 / 일회성 결제 토글) ── */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white/[0.02] border border-white/5 p-4 rounded-2xl">
              <div className="text-left">
                <p className="text-xs font-bold text-white">
                  {userProfile?.uiLanguage === 'ko' ? '결제 방식 선택' : 'Choose Payment Type'}
                </p>
                <p className="text-[10px] text-white/45 mt-0.5">
                  {userProfile?.uiLanguage === 'ko'
                    ? '정기 구독은 자동으로 갱신되며, 일회성 결제는 1회만 결제(30일/365일 이용) 후 자동 만료됩니다.'
                    : 'Auto-renewal automatically bills recurring cycles, while One-time payment expires after access ends.'}
                </p>
              </div>
              <div className="flex p-1 rounded-xl bg-zinc-900 border border-white/5 self-start sm:self-auto">
                <button
                  onClick={() => setPaymentType('subscription')}
                  className={`px-4 py-1.5 rounded-lg text-xs font-black transition-all ${paymentType === 'subscription'
                    ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-md'
                    : 'text-white/40 hover:text-white/70'
                    }`}
                >
                  {userProfile?.uiLanguage === 'ko' ? '정기 구독 결제' : 'Auto-Subscription'}
                </button>
                <button
                  onClick={() => setPaymentType('onetime')}
                  className={`px-4 py-1.5 rounded-lg text-xs font-black transition-all ${paymentType === 'onetime'
                    ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-md'
                    : 'text-white/40 hover:text-white/70'
                    }`}
                >
                  {userProfile?.uiLanguage === 'ko' ? '1회성 일반 결제' : 'One-time Payment'}
                </button>
              </div>
            </div>

            {/* ── 양측 2열 레이아웃 컨테이너 ── */}
            <div className="flex flex-col md:flex-row gap-8 w-full">
              {/* Left: Value Proposition (1단계: 명확한 가치 비교) */}
              <div className="flex-1 flex flex-col gap-6 text-left border-b md:border-b-0 md:border-r border-white/10 pb-6 md:pb-0 md:pr-8 text-white">
                <div className="space-y-1">
                  <span className="text-[10px] font-black uppercase tracking-wider text-indigo-400">Value Proposition</span>
                  <h3 className="text-xl font-black text-white flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-indigo-400" />
                    {userProfile?.uiLanguage === 'ko' ? '더 강력해지는 분석 파워' : 'Unlock Extra Analysis Power'}
                  </h3>
                  {/* 대상 등급 인라인 뱃지 */}
                  <div className="flex items-center gap-2 mt-2">
                    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[11px] font-black ${targetCfg.borderColor} bg-gradient-to-r ${targetCfg.bgGradient}`}>
                      {targetCfg.icon}
                      <span className={targetCfg.color}>{targetTierName}</span>
                    </div>
                    <span className="text-white/25 text-[10px]">{userProfile?.uiLanguage === 'ko' ? '으로 업그레이드' : 'upgrade target'}</span>
                  </div>
                  <p className="text-white/50 text-xs leading-relaxed mt-1 font-medium">
                    {userProfile?.uiLanguage === 'ko'
                      ? `선택하신 등급으로 업그레이드하면 제공 한도가 대폭 확장되며 프리미엄 분석 도구가 해금됩니다.`
                      : `Upgrading to the next tier increases your system limits and unlocks macro quant capabilities.`}
                  </p>
                </div>

                {/* 핵심 차이점 비교 카드 */}
                <div className="space-y-3 bg-white/[0.02] border border-white/5 p-5 rounded-2xl">
                  <h4 className="text-[11px] font-black text-white/30 uppercase tracking-widest">핵심 스펙 체인지</h4>
                  <div className="space-y-3.5 text-xs">
                    <div className="flex justify-between items-center pb-2.5 border-b border-white/[0.03]">
                      <span className="text-white/40">{userProfile?.uiLanguage === 'ko' ? '보유 종목 한도' : 'Holdings Limit'}</span>
                      <div className="flex items-center gap-2 text-white font-bold">
                        <span className="opacity-40 line-through">{currentCfg.perks[0]?.split(' ')[1] || '5개'}</span>
                        <ArrowRight className="w-3.5 h-3.5 text-indigo-400" />
                        <span className="text-emerald-400 font-extrabold">{targetCfg.perks[0]}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center pb-2.5 border-b border-white/[0.03]">
                      <span className="text-white/40">{userProfile?.uiLanguage === 'ko' ? 'AI 리포트 및 리스크 알림' : 'Key Perks'}</span>
                      <div className="flex flex-col items-end gap-0.5 text-right font-medium">
                        <span className="text-[10px] opacity-40 line-through">{currentCfg.perks[1]}</span>
                        <span className="text-emerald-400 font-bold">{targetCfg.perks[1]}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-white/40">{userProfile?.uiLanguage === 'ko' ? '특화 모델 백테스트' : 'Model Access'}</span>
                      <div className="flex flex-col items-end gap-0.5 text-right font-medium">
                        <span className="text-[10px] opacity-40 line-through">{currentCfg.perks[2] || '미지원'}</span>
                        <span className="text-indigo-400 font-bold">{targetCfg.perks[2]}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 기능 상세 비교 리스트 (기존 기능 블러 처리) */}
                <div className="space-y-2">
                  <h4 className="text-[11px] font-black text-white/30 uppercase tracking-widest">{userProfile?.uiLanguage === 'ko' ? '해금되는 혜택' : 'New Benefits'}</h4>
                  <div className="grid grid-cols-1 gap-2">
                    {targetCfg.perks.map((perk) => {
                      const isNewPerk = !currentCfg.perks.includes(perk);
                      return (
                        <div
                          key={perk}
                          className={`flex items-center gap-2 text-xs p-2.5 rounded-xl border ${isNewPerk
                            ? 'bg-emerald-500/5 border-emerald-500/10 text-white font-semibold'
                            : 'bg-white/[0.01] border-white/5 text-white/30 opacity-40'
                            }`}
                        >
                          <CheckCircle2 className={`w-4 h-4 shrink-0 ${isNewPerk ? 'text-emerald-400' : 'text-white/20'}`} />
                          <span>{perk}</span>
                          {isNewPerk && (
                            <span className="ml-auto text-[9px] bg-emerald-500/20 text-emerald-300 font-black px-1.5 py-0.5 rounded uppercase tracking-wider">NEW</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Right: Proration Invoice & Checkout (2단계: 투명한 금액 안내) */}
              <div className="flex-1 flex flex-col gap-6 text-left justify-between min-h-[380px]">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <span className="text-[10px] font-black uppercase tracking-wider text-rose-400">Prorated Invoice</span>
                    <h3 className="text-lg font-black text-white">
                      {userProfile?.uiLanguage === 'ko' ? '일할 계산 영수증' : 'Pro-rated Billing Invoice'}
                    </h3>
                    {/* 결제 대상 등급 명칭 — 영수증 헤더에 명확히 표시 */}
                    <div className="flex items-center gap-2 flex-wrap mt-2">
                      <span className="text-[10px] text-white/30">{userProfile?.uiLanguage === 'ko' ? '결제 대상:' : 'Target:'}</span>
                      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[11px] font-black ${targetCfg.borderColor} bg-gradient-to-r ${targetCfg.bgGradient}`}>
                        {targetCfg.icon}
                        <span className={targetCfg.color}>{targetTierName}</span>
                      </div>
                      <span className="text-[10px] text-white/25 font-mono">· {billingCycle === 'annual' ? (userProfile?.uiLanguage === 'ko' ? '연간 결제' : 'Annual Billing') : (userProfile?.uiLanguage === 'ko' ? '월간 결제' : 'Monthly Billing')}</span>
                    </div>
                  </div>

                  {isLoadingPreview ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-2 text-white/40">
                      <RefreshCcw className="w-8 h-8 animate-spin text-indigo-400" />
                      <p className="text-xs">{userProfile?.uiLanguage === 'ko' ? '요금 일할 계산 정보를 조회하는 중...' : 'Calculating pro-rated values...'}</p>
                    </div>
                  ) : receipt ? (
                    <div className="bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden">
                      <div className="p-4 space-y-3.5 text-xs text-white/70">
                        <div className="flex justify-between">
                          <span className="opacity-60">{userProfile?.uiLanguage === 'ko' ? '업그레이드 등급 요금 (월)' : 'Upgrade Target Plan (Monthly)'}</span>
                          <span className="font-semibold text-white">{receipt.targetPrice}</span>
                        </div>
                        <div className="flex justify-between text-emerald-400 border-b border-white/[0.03] pb-3.5">
                          <span className="opacity-90">{userProfile?.uiLanguage === 'ko' ? '기존 요금제 남은 기간 환불' : 'Prorated Unused Balance Refund'}</span>
                          <span className="font-semibold">-{receipt.refundAmount}</span>
                        </div>
                        <div className="flex justify-between items-center pt-2">
                          <span className="text-white font-extrabold text-sm">{userProfile?.uiLanguage === 'ko' ? '오늘 결제할 금액' : 'Due Today (Prorated)'}</span>
                          <span className="text-2xl font-black text-rose-400 tracking-tight">{receipt.amountToPay}</span>
                        </div>
                      </div>

                      <div className="bg-white/[0.02] border-t border-white/5 p-4 text-[10.5px] text-white/40 leading-relaxed font-medium">
                        💡 {userProfile?.uiLanguage === 'ko'
                          ? `오늘 결제 이후 기존의 결제 주기가 만료되는 ${receipt.nextBillingDate}에 ${receipt.targetPrice}으로 정상 정기 자동 결제됩니다.`
                          : `Your billing cycle is preserved. On ${receipt.nextBillingDate}, you will be automatically billed ${receipt.targetPrice}.`}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 p-4 bg-rose-500/10 border border-rose-500/20 rounded-2xl text-rose-400 text-xs">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <span>{userProfile?.uiLanguage === 'ko' ? '일할 계산서 파싱에 실패했습니다.' : 'Failed to parse billing receipt details.'}</span>
                    </div>
                  )}

                  {/* 3단계: 원클릭 결제 (Frictionless Payment) */}
                  {paymentType === 'subscription' && (
                    userProfile?.hasBillingKey ? (
                      <div className="bg-indigo-500/5 border border-indigo-500/10 p-4 rounded-2xl space-y-2">
                        <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">기존 등록 결제 수단</p>
                        <div className="flex justify-between text-xs text-white/70">
                          <span className="font-bold text-white">{userProfile.cardCompany} 카드</span>
                          <span className="opacity-50">{userProfile.cardNumber}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 bg-zinc-900 border border-white/5 rounded-2xl text-center">
                        <p className="text-xs text-white/50">{userProfile?.uiLanguage === 'ko' ? '아직 등록된 결제 수단이 없습니다.' : 'No cards registered yet.'}</p>
                      </div>
                    )
                  )}
                </div>

                <div className="space-y-2">
                  {paymentType === 'onetime' ? (
                    <button
                      disabled={isPaying || isLoadingPreview}
                      onClick={handleTossOneTimePaymentCall}
                      className="w-full py-4 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 disabled:opacity-40 text-white font-extrabold rounded-2xl transition-all shadow-lg shadow-indigo-600/20 flex items-center justify-center gap-2 text-sm cursor-pointer"
                    >
                      {isPaying ? (
                        <>
                          <RefreshCcw className="w-4 h-4 animate-spin" />
                          {userProfile?.uiLanguage === 'ko' ? '결제창 여는 중...' : 'Opening gateway...'}
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4.5 h-4.5" />
                          {userProfile?.uiLanguage === 'ko' ? '1회성 일반 결제하기' : 'Pay One-Time'}
                        </>
                      )}
                    </button>
                  ) : userProfile?.hasBillingKey ? (
                    <button
                      disabled={isPaying || isLoadingPreview}
                      onClick={handleDirectProratedUpgrade}
                      className="w-full py-4 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 disabled:opacity-40 text-white font-extrabold rounded-2xl transition-all shadow-lg shadow-indigo-600/20 flex items-center justify-center gap-2 text-sm cursor-pointer"
                    >
                      {isPaying ? (
                        <>
                          <RefreshCcw className="w-4 h-4 animate-spin" />
                          {userProfile?.uiLanguage === 'ko' ? '결제 승인 중...' : 'Authorizing Card...'}
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4.5 h-4.5" />
                          {userProfile?.uiLanguage === 'ko' ? '기존 카드로 원클릭 결제하기' : 'Pay Instantly with Registered Card'}
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      disabled={isPaying || isLoadingPreview}
                      onClick={handleTossBillingAuthCall}
                      className="w-full py-4 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 disabled:opacity-40 text-white font-extrabold rounded-2xl transition-all shadow-lg shadow-indigo-600/20 flex items-center justify-center gap-2 text-sm cursor-pointer"
                    >
                      {isPaying ? (
                        <>
                          <RefreshCcw className="w-4 h-4 animate-spin" />
                          {userProfile?.uiLanguage === 'ko' ? '모듈 여는 중...' : 'Opening gateway...'}
                        </>
                      ) : (
                        <>
                          <ArrowRight className="w-4.5 h-4.5" />
                          {userProfile?.uiLanguage === 'ko' ? '새 카드 등록 및 결제하기' : 'Register New Card & Checkout'}
                        </>
                      )}
                    </button>
                  )}
                  <p className="text-center text-[10px] text-white/25">
                    현재 멤버십 등급: <span className="text-white/40 font-bold">{currentCfg.label}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 4단계: 즉각적인 성공 피드백과 온보딩 (View 2: Success) */}
        {activeStep === 'success' && (
          <div className="w-full flex flex-col items-center justify-center py-12 text-center gap-6 relative min-h-[380px]">

            {/* 폭죽 애니메이션을 가볍게 재현 (색종이 가루) */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              {[...Array(24)].map((_, idx) => {
                const colors = ['bg-rose-500', 'bg-violet-500', 'bg-amber-400', 'bg-emerald-400', 'bg-cyan-400'];
                const randColor = colors[idx % colors.length];
                const startX = 40 + Math.random() * 20; // center
                const delay = Math.random() * 0.5;
                const duration = 1.5 + Math.random() * 1;
                return (
                  <div
                    key={idx}
                    className={`absolute w-2 h-2 rounded-full ${randColor} animate-ping`}
                    style={{
                      left: `${startX - 30 + Math.random() * 60}%`,
                      top: `${10 + Math.random() * 40}%`,
                      animationDelay: `${delay}s`,
                      animationDuration: `${duration}s`
                    }}
                  />
                );
              })}
            </div>

            <div className="w-20 h-20 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center relative">
              <Sparkles className="w-10 h-10 text-emerald-400 animate-pulse" />
              <div className="absolute -top-1 -right-1 bg-violet-600 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full uppercase animate-bounce">
                UP
              </div>
            </div>

            <div className="space-y-2.5 max-w-lg">
              <h3 className="text-2xl font-black text-white">
                {userProfile?.uiLanguage === 'ko' ? '멤버십 등급 업그레이드 완료!' : 'Membership Upgraded!'}
              </h3>
              <p className="text-white/60 text-sm leading-relaxed">
                {userProfile?.uiLanguage === 'ko'
                  ? `성공적으로 결제가 완료되었습니다. 이제 ${targetCfg.label}의 모든 핵심 제한 한도가 해금되었으며, 실시간 리밸런싱 시뮬레이션 및 마이크로 AI 분석 혜택을 이용하실 수 있습니다.`
                  : `Your plan has been upgraded to ${targetCfg.label}. All restricted limits and premium features are now unlocked for immediate use.`}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-4 w-full max-w-md">
              <button
                onClick={() => {
                  onClose();
                  // 해금된 최적화 리밸런싱 탭으로 리다이렉트
                  onNavigateTo?.('simulator');
                }}
                className="flex-1 py-3.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-zinc-950 font-black rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer text-xs"
              >
                {userProfile?.uiLanguage === 'ko' ? 'AI 최적 리밸런싱 써보기' : 'Try AI Rebalancing'}
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={onClose}
                className="flex-1 py-3.5 bg-white/5 hover:bg-white/10 text-white font-bold rounded-xl transition-all border border-white/5 cursor-pointer text-xs"
              >
                {userProfile?.uiLanguage === 'ko' ? '창 닫기' : 'Close Window'}
              </button>
            </div>
          </div>
        )}

        {/* View 3: Failure (결제 실패 처리) */}
        {activeStep === 'fail' && (
          <div className="w-full flex flex-col items-center justify-center py-12 text-center gap-6 min-h-[380px]">
            <div className="w-16 h-16 rounded-full bg-rose-500/10 border border-rose-500/30 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-rose-400" />
            </div>

            <div className="space-y-2.5 max-w-lg">
              <h3 className="text-xl font-black text-white">
                {userProfile?.uiLanguage === 'ko' ? '구독 변경 결제 승인 실패' : 'Card Authorization Failed'}
              </h3>
              <p className="text-white/60 text-xs leading-relaxed">
                {errorMessage || (userProfile?.uiLanguage === 'ko'
                  ? '기존 등록 카드의 한도 초과, 유효기간 만료 또는 카드사 거절 오류가 발생했습니다. 서비스 혜택은 안전하게 기존 등급 상태로 유지 중입니다.'
                  : 'We were unable to charge your registered card. Your current tier membership and privileges remain unaffected.')}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-4 w-full max-w-md">
              <button
                onClick={() => {
                  onClose();
                  onNavigateTo?.('settings');
                }}
                className="flex-1 py-3.5 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-400 hover:to-pink-400 text-white font-black rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer text-xs"
              >
                {userProfile?.uiLanguage === 'ko' ? '결제 카드 정보 변경하기' : 'Update Card Details'}
              </button>
              <button
                onClick={() => setActiveStep('checkout')}
                className="flex-1 py-3.5 bg-white/5 hover:bg-white/10 text-white font-bold rounded-xl transition-all border border-white/5 cursor-pointer text-xs"
              >
                {userProfile?.uiLanguage === 'ko' ? '다시 시도' : 'Try Again'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </div>,
    document.body
  );
}

// ─── TierGate ─────────────────────────────────────────────────────────────────

interface TierGateProps {
  /** 이 기능에 필요한 최소 등급 */
  requiredTier: MembershipTier;
  /** 현재 사용자 등급 */
  currentTier: MembershipTier;
  /** 잠긴 기능의 이름 (업그레이드 모달 표시용) */
  featureName?: string;
  /** 잠긴 상태에서 보여줄 자식 (블러 처리됨) */
  children: React.ReactNode;
  /** 업그레이드 성공 시 콜백 */
  onUpgradeSuccess?: () => void;
  /** 네비게이션 콜백 */
  onNavigateTo?: (tab: string) => void;
  /** 사용자 프로필 */
  userProfile?: UserProfile | null;
  /** 잠금 뱃지 위치 */
  badgePosition?: 'center' | 'top-right';
  /** blur 강도 */
  blurLevel?: 'sm' | 'md' | 'lg';
  /** pointer-events 차단 여부 (기본 true) */
  blockInteraction?: boolean;
}

export function TierGate({
  requiredTier,
  currentTier,
  featureName = '이 기능',
  children,
  onUpgradeSuccess,
  onNavigateTo,
  userProfile,
  badgePosition = 'center',
  blurLevel = 'md',
  blockInteraction = true,
}: TierGateProps) {
  const [showModal, setShowModal] = useState(false);

  // 접근 가능한 경우 그대로 렌더링
  if (canAccessTier(currentTier, requiredTier)) {
    return <>{children}</>;
  }

  const cfg = TIER_CONFIG[requiredTier] || TIER_CONFIG.TIER2;
  const blurClass = { sm: 'blur-[1px]', md: 'blur-[2px]', lg: 'blur-[4px]' }[blurLevel];

  return (
    <>
      {showModal && (
        <UpgradeModal
          requiredTier={requiredTier}
          featureName={featureName}
          currentTier={currentTier}
          userProfile={userProfile}
          onClose={() => setShowModal(false)}
          onUpgradeSuccess={() => {
            setShowModal(false);
            onUpgradeSuccess?.();
          }}
          onNavigateTo={(tab) => {
            setShowModal(false);
            onNavigateTo?.(tab);
          }}
        />
      )}

      <div
        className="relative cursor-pointer select-none"
        onClick={() => setShowModal(true)}
        role="button"
        aria-label={`${featureName} — 업그레이드 필요`}
      >
        {/* 흐린 자식 콘텐츠 */}
        <div
          className={`
            opacity-30 ${blurClass} transition-all
            ${blockInteraction ? 'pointer-events-none' : ''}
          `}
        >
          {children}
        </div>

        {/* 잠금 뱃지 */}
        {badgePosition === 'center' ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className={`
                flex items-center gap-1.5 px-3 py-1.5 rounded-xl
                bg-gradient-to-r ${cfg.bgGradient} border ${cfg.borderColor}
                shadow-lg backdrop-blur-sm
              `}
            >
              <Lock className={`w-3 h-3 ${cfg.color}`} />
              <span className={`text-[10px] font-black tracking-wider ${cfg.color}`}>
                {userProfile?.uiLanguage === 'ko' ? `${cfg.label} 혜택 해금하기` : `Unlock ${cfg.label} Perk`}
              </span>
            </div>
          </div>
        ) : (
          <div className="absolute top-2 right-2">
            <div
              className={`
                flex items-center gap-1 px-2 py-1 rounded-lg
                bg-gradient-to-r ${cfg.bgGradient} border ${cfg.borderColor}
              `}
            >
              <Lock className={`w-2.5 h-2.5 ${cfg.color}`} />
              <span className={`text-[9px] font-black ${cfg.color}`}>{cfg.label}</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ─── UsageBadge — AI 사용량 표시 뱃지 ───────────────────────────────────────

interface UsageBadgeProps {
  used: number;
  limit: number;
  lang?: string;
}

export function UsageBadge({ used, limit, lang = 'ko' }: UsageBadgeProps) {
  if (limit === -1) return null; // 무제한이면 표시 안 함

  const pct = Math.min(100, (used / limit) * 100);
  const isNear = pct >= 80;
  const isMax = used >= limit;

  return (
    <div className="flex items-center gap-1.5 text-[10px] font-medium text-white/40">
      <div className="w-16 h-1 bg-white/10 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${isMax ? 'bg-red-500' : isNear ? 'bg-amber-500' : 'bg-emerald-500'
            }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={isMax ? 'text-red-400 font-bold' : isNear ? 'text-amber-400' : ''}>
        {lang === 'ko'
          ? `${used}/${limit}회`
          : `${used}/${limit}`}
      </span>
      {isMax && (
        <span className="text-red-400 font-bold">
          {lang === 'ko' ? '한도 초과' : 'Limit reached'}
        </span>
      )}
    </div>
  );
}

// ─── TierBadge — 현재 등급 표시 뱃지 ───────────────────────────────────────

interface TierBadgeProps {
  tier: MembershipTier;
  size?: 'sm' | 'md';
}

export function TierBadge({ tier, size = 'md' }: TierBadgeProps) {
  const cfg = TIER_CONFIG[tier] || TIER_CONFIG.TIER1;
  const sizeClass = size === 'sm'
    ? 'text-[8px] px-1.5 py-0.5 gap-1'
    : 'text-[10px] px-2 py-1 gap-1.5';

  return (
    <span
      className={`
        inline-flex items-center rounded-lg border font-black uppercase tracking-wider
        bg-gradient-to-r ${cfg.bgGradient} ${cfg.borderColor} ${cfg.color} ${sizeClass}
      `}
    >
      {cfg.icon}
      {cfg.label}
    </span>
  );
}

// ─── AddonPaymentModal — 부가상품 결제 상세 팝업 ───────────────────────────

interface AddonPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  addonId: string;
  userProfile?: UserProfile | null;
  onConfirmPayment: (addonId: string, useExistingCard: boolean, paymentType: 'subscription' | 'onetime') => void;
  isPaying?: boolean;
}

// ADDON_FALLBACK_CATALOG removed to enforce DB-Driven UI

export function AddonPaymentModal({
  isOpen,
  onClose,
  addonId,
  userProfile,
  onConfirmPayment,
  isPaying = false
}: AddonPaymentModalProps) {
  const [addonConfig, setAddonConfig] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [useExistingCard, setUseExistingCard] = useState(true);
  const [paymentType, setPaymentType] = useState<'subscription' | 'onetime'>('subscription');

  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setIsLoading(true);
    setErrorMsg(null);
    setAddonConfig(null);

    fetch(`/api/addon-config?id=${addonId}`)
      .then(res => res.json())
      .then((data: any) => {
        if (data && !data.error && data.name) {
          setAddonConfig(data);
        } else {
          setErrorMsg('부가상품 정보를 불러오지 못했습니다.');
        }
      })
      .catch(err => {
        console.error("Failed to load addon config:", err);
        setErrorMsg('부가상품 정보를 불러오지 못했습니다.');
      })
      .finally(() => setIsLoading(false));
  }, [isOpen, addonId]);

  if (!isOpen) return null;

  const isKo = userProfile?.uiLanguage === 'ko';
  const priceKrw = addonConfig?.price || 0;
  const priceUsd = addonConfig?.priceUsd || 0;

  return createPortal(
    <div className="fixed inset-0 z-[250] flex items-center justify-center bg-black/85 backdrop-blur-md px-4">
      <div className="relative w-full max-w-lg bg-zinc-950 border border-white/10 rounded-3xl p-6 sm:p-8 overflow-hidden shadow-2xl shadow-indigo-500/10">

        {/* Close Button */}
        <button
          onClick={onClose}
          disabled={isPaying}
          className="absolute top-5 right-5 p-1.5 rounded-full bg-white/[0.03] hover:bg-white/[0.08] text-white/55 hover:text-white transition-all border border-white/5 disabled:opacity-30 cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="text-center mt-2 mb-6">
          <div className="inline-flex p-3 rounded-2xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white mb-4 shadow-lg shadow-indigo-600/30">
            <Sparkles className="w-6 h-6" />
          </div>
          <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight">
            {isKo ? '부가상품 정기 구독 신청' : 'Add-on Subscription Checkout'}
          </h2>
          <p className="text-xs text-white/40 mt-1.5 font-medium">
            {isKo ? '선택하신 프리미엄 부가서비스를 연동하여 시스템을 확장합니다.' : 'Extend your system features with this premium addon.'}
          </p>
        </div>

        {/* Product Details Card */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <RefreshCcw className="w-8 h-8 text-indigo-400 animate-spin mb-3" />
            <span className="text-[10px] text-white/40 font-black tracking-widest uppercase">Loading details...</span>
          </div>
        ) : addonConfig ? (
          <div className="space-y-6 text-left">

            {/* Spec Card */}
            <div className="bg-white/[0.02] border border-white/5 p-5 rounded-2xl space-y-4">
              <div>
                <p className="text-[9px] font-black text-indigo-400 uppercase tracking-widest mb-1">{isKo ? '결제 대상 부가상품' : 'Subscription Item'}</p>
                <h3 className="text-base font-extrabold text-white">{addonConfig.name}</h3>
                <p className="text-xs text-white/55 mt-1 leading-relaxed font-medium">{addonConfig.description}</p>
              </div>

              <div className="h-px bg-white/5" />

              {/* Price Details */}
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-[9px] font-black text-white/30 uppercase tracking-widest">{isKo ? '월 구독 결제 금액' : 'Monthly Payment Rate'}</p>
                  <p className="text-[10px] text-white/40 mt-0.5">{isKo ? '실제 결제 승인 요청 금액 (정기 자동 청구)' : 'Actual charge amount for recurring billing'}</p>
                </div>
                <div className="text-right">
                  {userProfile?.currencyPreference === 'USD' ? (
                    <div className="text-xl font-black text-emerald-400 font-mono">
                      ${priceUsd.toFixed(2)} <span className="text-xs text-white/40 font-normal">/ {isKo ? '월' : 'mo'}</span>
                    </div>
                  ) : (
                    <div className="text-xl font-black text-emerald-400 font-mono">
                      ₩{priceKrw.toLocaleString()} <span className="text-xs text-white/40 font-normal">/ {isKo ? '월' : 'mo'}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 결제 방식 선택 */}
            <div className="bg-white/[0.02] border border-white/5 p-4 rounded-2xl">
              <label className="text-xs font-bold text-white/40 block mb-2">{isKo ? '결제 방식 선택' : 'Payment Type'}</label>
              <div className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-zinc-900 border border-white/5">
                <button
                  type="button"
                  onClick={() => setPaymentType('subscription')}
                  className={`py-2 rounded-lg text-xs font-black transition-all ${paymentType === 'subscription' ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-md' : 'text-white/40 hover:text-white/70'}`}
                >
                  {isKo ? '정기 구독 결제' : 'Auto-Subscription'}
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentType('onetime')}
                  className={`py-2 rounded-lg text-xs font-black transition-all ${paymentType === 'onetime' ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-md' : 'text-white/40 hover:text-white/70'}`}
                >
                  {isKo ? '1회성 일반 결제' : 'One-time Payment'}
                </button>
              </div>
            </div>

            {/* Existing Card Checkbox */}
            {paymentType === 'subscription' && (
              userProfile?.hasBillingKey ? (
                <div className="bg-indigo-500/5 border border-indigo-500/10 p-5 rounded-2xl">
                  <label className="flex items-center gap-3 cursor-pointer select-none text-left">
                    <input
                      type="checkbox"
                      checked={useExistingCard}
                      onChange={(e) => setUseExistingCard(e.target.checked)}
                      disabled={isPaying}
                      className="w-4 h-4 rounded border-white/10 bg-black/50 text-indigo-500 focus:ring-indigo-500/20"
                    />
                    <div>
                      <p className="text-xs font-black text-white">{isKo ? '기존 등록된 카드로 즉시 결제' : 'Pay with registered card'}</p>
                      <p className="text-[10px] text-indigo-300/60 font-mono font-bold mt-0.5">
                        {userProfile.cardCompany} 카드 ({userProfile.cardNumber})
                      </p>
                    </div>
                  </label>
                </div>
              ) : (
                <div className="bg-white/[0.01] border border-white/5 p-4 rounded-2xl text-center">
                  <p className="text-xs text-white/40 leading-relaxed">
                    {isKo ? '아직 등록된 결제 카드가 없습니다.\n첫 결제 시 카드가 안전하게 자동 등록됩니다.' : 'No registered card.\nYour card will be secure and saved during checking out.'}
                  </p>
                </div>
              )
            )}

            {/* Action Button */}
            <div className="pt-2">
              {errorMsg && (
                <div className="mb-3 text-center text-xs font-bold text-red-400 bg-red-500/10 p-2 rounded-lg border border-red-500/20">
                  {errorMsg}
                </div>
              )}
              <button
                disabled={isPaying || isLoading || !!errorMsg || !addonConfig}
                onClick={() => addonConfig && onConfirmPayment(addonId, useExistingCard, paymentType)}
                className="w-full py-4 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 disabled:opacity-40 text-white font-extrabold rounded-2xl transition-all shadow-lg shadow-indigo-600/20 flex items-center justify-center gap-2 text-sm cursor-pointer"
              >
                {isPaying ? (
                  <>
                    <RefreshCcw className="w-4 h-4 animate-spin" />
                    {isKo ? '결제 승인 중...' : 'Authorizing...'}
                  </>
                ) : paymentType === 'onetime' ? (
                  <>
                    <CheckCircle2 className="w-4.5 h-4.5" />
                    {isKo ? '1회성 일반 결제하기' : 'Pay One-Time'}
                  </>
                ) : userProfile?.hasBillingKey && useExistingCard ? (
                  <>
                    <CheckCircle2 className="w-4.5 h-4.5" />
                    {isKo ? '즉시 구독 결제하기' : 'Pay & Subscribe Now'}
                  </>
                ) : (
                  <>
                    <ArrowRight className="w-4.5 h-4.5" />
                    {isKo ? '카드 등록 및 구독 결제하기' : 'Register Card & Checkout'}
                  </>
                )}
              </button>
            </div>

          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-rose-400 gap-2">
            <AlertCircle className="w-8 h-8" />
            <span className="text-xs font-bold">{isKo ? '상품 정보를 불러오지 못했습니다.' : 'Failed to load addon information.'}</span>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
